import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Typography, 
    Chip, 
    TablePagination,
    Skeleton,
    Button,
    Select,
    MenuItem,
    CircularProgress,
    IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { RestaurantService } from '../../../api/services/restaurantService';
import CreateTableModal from './CreateTableModal';
import EditTableModal from './EditTableModal';
import { extractTotalCount } from '../../../components/common/tableStyles';
import { toast } from '../../../utils/toast';

export default function TablesTab() {
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [updatingTableId, setUpdatingTableId] = useState(null);
    
    // Edit Modal states
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);

    // Pagination & Filters
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);

    // Fetch data
    const fetchTables = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: limit
            };

            const response = await RestaurantService.getRestaurantTables(params);
            
            const dataArray = response?.data || response?.tables || [];
            setTables(Array.isArray(dataArray) ? dataArray : []);
            setTotalRecords(extractTotalCount(response, dataArray));
        } catch (error) {
            console.error('Failed to fetch restaurant tables:', error);
            // Handled by global interceptor
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTables();
    }, [page, limit]);

    const getStatusColor = (status) => {
        const s = status ? status.toLowerCase() : '';
        if (s === 'vacant' || s === 'available') return 'success';
        if (s === 'occupied') return 'error';
        if (s === 'reserved') return 'warning';
        return 'default';
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    
    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleStatusChange = async (tableId, newStatus) => {
        setUpdatingTableId(tableId);
        try {
            await RestaurantService.updateRestaurantTable(tableId, { status: newStatus });
            toast.success('Table availability status updated successfully');
            fetchTables();
        } catch (error) {
            console.error('Failed to update table status:', error);
            // Re-fetch to ensure local state resets if update failed
            fetchTables(); 
        } finally {
            setUpdatingTableId(null);
        }
    };

    const handleEditClick = (table) => {
        setSelectedTable(table);
        setEditModalOpen(true);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                    Floor Plan Master Directory
                </Typography>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                    onClick={() => setCreateModalOpen(true)}
                >
                    Create New Table
                </Button>
            </Box>

            {/* Data Grid */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Table Number / Label</TableCell>
                            <TableCell>Seating Capacity</TableCell>
                            <TableCell>Current Availability</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from(new Array(limit)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="40%" /></TableCell>
                                    <TableCell><Skeleton width={80} sx={{ display: 'inline-block' }} /></TableCell>
                                    <TableCell><Skeleton width={40} sx={{ display: 'inline-block', float: 'right' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : tables.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">No tables found matching the query.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            tables.map((table, idx) => (
                                <TableRow key={table.id || idx} hover>
                                    <TableCell>
                                        <Typography variant="body1" fontWeight="bold">
                                            {table.tableNumber || table.label || table.name || `Table ${idx + 1}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {table.capacity || 0}-seater
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {updatingTableId === table.id ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={20} />
                                                <Typography variant="body2" color="text.secondary">Updating...</Typography>
                                            </Box>
                                        ) : (
                                            <Select
                                                size="small"
                                                value={table.status || 'Available'}
                                                onChange={(e) => handleStatusChange(table.id, e.target.value)}
                                                sx={{ 
                                                    minWidth: 140, 
                                                    height: 32,
                                                    fontSize: '0.85rem',
                                                    fontWeight: 'bold',
                                                    color: getStatusColor(table.status || 'Available') === 'success' ? 'success.main' : 
                                                           getStatusColor(table.status || 'Available') === 'error' ? 'error.main' : 'warning.main',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'transparent'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#e0e0e0'
                                                    },
                                                    bgcolor: 'rgba(0,0,0,0.02)'
                                                }}
                                            >
                                                <MenuItem value="Available"><Typography color="success.main" variant="body2" fontWeight="bold">Available</Typography></MenuItem>
                                                <MenuItem value="Occupied"><Typography color="error.main" variant="body2" fontWeight="bold">Occupied</Typography></MenuItem>
                                                <MenuItem value="Reserved"><Typography color="warning.main" variant="body2" fontWeight="bold">Reserved</Typography></MenuItem>
                                                <MenuItem value="Dirty / Clearing"><Typography color="text.secondary" variant="body2" fontWeight="bold">Dirty / Clearing</Typography></MenuItem>
                                            </Select>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleEditClick(table)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                
                {/* Pagination Controls Footer */}
                <TablePagination
                    component="div"
                    count={tables.length === 0 ? 0 : totalRecords}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <CreateTableModal 
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSuccess={() => fetchTables()}
            />

            <EditTableModal 
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setSelectedTable(null);
                }}
                onSuccess={() => fetchTables()}
                table={selectedTable}
            />
        </Box>
    );
}
